import halkaBox from "halkabox";
import domready from "domready";

/**
 * Stores the element that had focus before the lightbox opened.
 * @type {HTMLElement | null}
 */
let lastFocusedElement = null;

/**
 * Returns the page language code used for translations.
 * Defaults to 'en' if language is not German.
 * @returns {'de' | 'en'}
 */
function getPageLanguage() {
  const htmlLang = document.documentElement.lang;
  return htmlLang?.toLowerCase().startsWith('de') ? 'de' : 'en';
}

/**
 * Gets the prefix label for the lightbox links based on language.
 * @param {'de' | 'en'} language
 * @returns {string}
 */
function getLightboxLabelPrefix(language) {
  const labels = {
    en: 'Open media in lightbox',
    de: 'Medien in der Lightbox öffnen',
  };
  return labels[language] || labels.en;
}

/**
 * Adds accessible aria-labels to lightbox links inside the given element.
 * It prepends a prefix indicating the link opens a lightbox.
 * If an aria-label exists, it is preserved and prefixed.
 * Otherwise, uses figcaption and copyright text for description.
 * @param {Element} el
 */
function addLabelsToLightboxLinks(el) {
  const language = getPageLanguage();
  const labelPrefix = getLightboxLabelPrefix(language);

  const lightboxLinks = el.querySelectorAll('a[class*="lightbox-"]');
  lightboxLinks.forEach((link) => {
    const existingLabel = link.getAttribute('aria-label')?.trim();

    if (existingLabel) {
      // Prefix existing label
      link.setAttribute('aria-label', `${labelPrefix}: ${existingLabel}`);
    } else {
      const figcaption = link.querySelector('figcaption')?.textContent?.trim();
      const copyright = link.querySelector('.copyright-text')?.textContent?.trim();

      const labelParts = [];
      if (figcaption) labelParts.push(figcaption);
      if (copyright) labelParts.push(copyright);

      const description = labelParts.length > 0 ? `: ${labelParts.join('. ')}` : '';
      link.setAttribute('aria-label', `${labelPrefix}${description}`);
    }
  });
}

/**
 * Finds the first focusable element within a given container.
 * @param {Element} container - The DOM element to search within.
 * @returns {HTMLElement | null} The first focusable element found, or null if none.
 */
function findFirstFocusableElement(container) {
  if (!container) return null;
  const focusableElements = container.querySelectorAll('button:not([disabled])');
  return focusableElements.length > 0 ? focusableElements[0] : null;
}

/**
 * Handles the focus when the lightbox opens.
 * @param {HTMLElement} lightboxContainer - The main .hb-wrapper element.
 */
function handleLightboxOpenFocus(lightboxContainer) {
  lastFocusedElement = document.activeElement;

  const firstFocusable = findFirstFocusableElement(lightboxContainer);
  if (firstFocusable) {
    requestAnimationFrame(() => {
      firstFocusable.focus();
    });
  } else {
    lightboxContainer.setAttribute('tabindex', '-1');
    lightboxContainer.focus();
  }
}

/**
 * Handles the focus when the lightbox closes.
 */
function handleLightboxCloseFocus() {
  if (lastFocusedElement && typeof lastFocusedElement.focus === 'function') {
    // Ensure the element is still in the DOM
    if (document.body.contains(lastFocusedElement)) {
      lastFocusedElement.focus();
    }
    lastFocusedElement = null;
  }
}

domready(() => {
  const potentialLightBoxElements = document.querySelectorAll('.gallery, .teaser, .textpic, .text-columns, .map, .image-row, .slider');
  if (!potentialLightBoxElements) return;

  potentialLightBoxElements.forEach((el) => {
    const lightboxID = el.dataset.lightbox || el.dataset.gallery;

    if (lightboxID) {
      halkaBox.run(lightboxID);
      addLabelsToLightboxLinks(el);

      const lightboxWrapper = document.getElementById(`hb-wrapper${lightboxID}`);

      if (lightboxWrapper) {
        // Use MutationObserver so we can track when the lightbox opens and closes (it does not dispatch any events we can listen to)
        const observer = new MutationObserver((mutationsList) => {
          // eslint-disable-next-line no-restricted-syntax
          for (const mutation of mutationsList) {
            if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
              const displayStyle = lightboxWrapper.style.display;
              const opacityStyle = lightboxWrapper.style.opacity;

              if (displayStyle === 'block' && opacityStyle === '1') {
                // When light box opens
                setTimeout(() => {
                  handleLightboxOpenFocus(lightboxWrapper);
                }, 50);
              } else if (displayStyle === 'none' && opacityStyle === '0') {
                // When light box closes
                setTimeout(() => {
                  handleLightboxCloseFocus();
                }, 350);
              }
            }
          }
        });

        // Start observing the lightbox wrapper for attribute changes
        observer.observe(lightboxWrapper, {
          attributes: true,
          attributeFilter: ['style'],
        });
      }
    }
  });
});
